/**
 * Iniciar sesión.
 *
 * La pantalla nunca dice si el correo existe: `INVALID_CREDENTIALS` es el
 * mismo mensaje para «no existe» y para «la contraseña está mal», porque
 * distinguirlos le regala al que prueba una lista de correos registrados.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Field } from '../components/Field';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { vieneDeUnCorreo } from '../lib/instalacion';

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * ADÓNDE IBA ANTES DE QUE LE PIDIÉRAMOS ENTRAR, si venía de algún lado.
   *
   * Lo deja `RequireAuth`. Sin esto, quien abre el enlace de una invitación
   * —o de un torneo— sin sesión entra y termina en la portada, sin lo que
   * había ido a ver.
   */
  const volverA = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSending(true);
    try {
      const response = await api.login({ email: email.trim(), password });
      signIn(response);
      navigate(volverA ? `${volverA.pathname}${volverA.search}` : '/', { replace: true });
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  const canSubmit = email.trim() !== '' && password !== '' && !sending;

  return (
    <AuthLayout>
      <h1>{t('login.title')}</h1>
      <p className="lead">{t('login.lead')}</p>

      {/*
        POR QUÉ TE PIDE ENTRAR SI YA ESTABAS ADENTRO.

        Quien toca un enlace de un correo en el teléfono cae acá aunque tenga
        la aplicación instalada y con sesión abierta: el navegador y la app
        instalada son dos lugares distintos y cada uno guarda la suya. En iOS
        no hay forma de evitarlo —Apple no deja que un enlace abra una
        aplicación agregada a la pantalla de inicio— así que lo único honesto
        es explicarlo.

        SE DICE UNA SOLA VEZ Y SOLO ACÁ: cuando se llega desde un correo y no
        hay sesión en este navegador. Quien entra por su cuenta no lo ve.

        SE MIRA LA MISMA MARCA QUE USA LA PUERTA DE INSTALACIÓN
        —`vieneDeUnCorreo`— y no una comprobación propia: son la misma
        pregunta, y dos formas de contestarla terminan diciendo cosas
        distintas.
      */}
      {vieneDeUnCorreo(window.location.search) && (
        <Alert kind="info">{t('login.fromEmail')}</Alert>
      )}

      {error !== null && <Alert kind="error">{error}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field id="login-email" label={t('login.email')}>
          {(control) => (
            <input
              {...control}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field id="login-password" label={t('login.password')}>
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <button type="submit" disabled={!canSubmit}>
          {sending ? t('common.sending') : t('login.submit')}
        </button>
      </form>

      <div className="links">
        <Link to="/forgot-password">{t('login.forgot')}</Link>
        <span>
          {t('login.noAccount')} <Link to="/register">{t('login.register')}</Link>
        </span>
      </div>
    </AuthLayout>
  );
}
